import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useEffect, useRef, useState } from "react";
import FormTextField from "../../components/formFields/FormTextField";
import {
  codeValidator,
  phoneValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { Button } from "@progress/kendo-react-buttons";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closed } from "../../components/drawer/drawerSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { DISC_TYPE, PROMO_TYPE, STATUSARRAY } from "../../_contstants/common";
import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";
import {
  createGiftCode,
  generateGiftCode,
  getAllGiftCodes,
  getGiftCodeById,
  updateGiftCode,
} from "./services/giftcode.services";
import { clearGiftCodeDetails, clearGiftCodeID } from "./giftcodeSlice";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormRadioGroup from "../../components/formFields/FormRadioGroup";
import { getCustomerDetailFromMobileNo } from "../userManagement/services/userManagement.services";
import FormCheckbox from "../../components/formFields/FormCheckbox";

interface TypeChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const TypeChangeWatcher: React.FC<TypeChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const dispatch = useAppDispatch();
  const GiftCodeID = useAppSelector((state) => state.drawer.data);
  const Type = formRenderProps.valueGetter("Type");

  useEffect(() => {
    const handlePromoCode = async () => {
      if (Type === "Auto" && !GiftCodeID) {
        const response = await dispatch(generateGiftCode());
        formRenderProps.onChange("GiftCode", { value: response.payload });
      }
    };

    handlePromoCode();
  }, [Type, dispatch]);

  return null;
};

const CrateGiftCode: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const GiftCodeDetail = useAppSelector(
    (state) => state.giftcode.GiftCodeDetail
  );
  const GiftCodeID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.giftcode.formLoading);
  const loading = useAppSelector((state) => state.userM.formloading);

  const [btnType, setBtnType] = useState(1);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    return () => {
      dispatch(clearGiftCodeDetails());
      dispatch(clearGiftCodeID());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (GiftCodeID) {
      dispatch(getGiftCodeById(GiftCodeID));
    }
  }, [GiftCodeID]);

  useEffect(() => {
    setFormKey((prevKey) => prevKey + 1);
  }, [GiftCodeDetail]);

  const handleGetUserDetails = async (
    mobileno: string,
    formRenderProps: FormRenderProps
  ) => {
    setBtnType(1);
    if (mobileno) {
      const response = await dispatch(getCustomerDetailFromMobileNo(mobileno));
      formRenderProps.onChange("CustomerDetails", { value: response.payload });
    }
  };

  const handleSubmit = async (values: any) => {
    if (btnType === 2) {
      try {
        if (!GiftCodeID) {
          const payload = {
            GiftCode: values?.GiftCode,
            FromDate: values?.FromDate
              ? moment(values?.FromDate).format("YYYY-MM-DD")
              : "",
            ToDate: values?.ToDate
              ? moment(values?.ToDate).format("YYYY-MM-DD")
              : "",
            Amount: values?.Amount,
            UsageCount: values?.UsageCount,
            OnlyUsedInWallet: values?.OnlyUsedInWallet || false,
            MinAmtUsage: values?.MinAmtUsage,
            MaxAmtUsage: values?.MaxAmtUsage,
            AmountType: values?.AmountType,
            FlightTicketAmt: values?.FlightTicketAmt,
            HotelBookingAmt: values?.HotelBookingAmt,
            AssignUserID: values?.CustomerDetails?.id,
            IsAuto: values?.Type === "Auto",
            IsActive: values.IsActive === 1,
          };
          const response = await dispatch(createGiftCode(payload));
          if (response?.meta?.requestStatus === "fulfilled") {
            dispatch(closed());
            dispatch(clearGiftCodeDetails());
            dispatch(getAllGiftCodes());
          }
        } else {
          const payload = {
            ID: GiftCodeID,
            GiftCode: values?.GiftCode,
            FromDate: values?.FromDate
              ? moment(values?.FromDate).format("YYYY-MM-DD")
              : "",
            ToDate: values?.ToDate
              ? moment(values?.ToDate).format("YYYY-MM-DD")
              : "",
            Amount: values?.Amount,
            UsageCount: values?.UsageCount,
            OnlyUsedInWallet: values?.OnlyUsedInWallet || false,
            MinAmtUsage: values?.MinAmtUsage,
            MaxAmtUsage: values?.MaxAmtUsage,
            AmountType: values?.AmountType,
            FlightTicketAmt: values?.FlightTicketAmt,
            HotelBookingAmt: values?.HotelBookingAmt,
            AssignUserID: values?.AssignUserID,
            IsAuto: values?.Type === "Auto",
            IsActive: values.IsActive === 1,
          };
          const response = await dispatch(updateGiftCode(payload));
          if (response?.meta?.requestStatus === "fulfilled") {
            dispatch(closed());
            dispatch(clearGiftCodeDetails());
            dispatch(getAllGiftCodes());
          }
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {(formLoading || loading) && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={GiftCodeDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                marginRight: 10,
              }}
              gap={{ rows: 10, cols: 10 }}
              cols={[{ width: "50%" }, { width: "50%" }]}
            >
              <GridLayoutItem className="master-drawer-header" colSpan={2}>
                <Typography.h4 className="m-0">
                  {GiftCodeID ? "Update Gift Code" : "Create Gift Code"}
                </Typography.h4>
                <Button
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                >
                  <RxCross1 />
                </Button>
              </GridLayoutItem>
              <GridLayoutItem
                colSpan={2}
                className="d-flex gap-3 align-items-start"
              >
                <Field
                  name="UserMobile"
                  type="number"
                  max="10"
                  wrapperClassName="w-50"
                  placeholder=" i.e 9999999999"
                  label="Mobile No"
                  astrike={true}
                  validator={phoneValidator}
                  component={FormTextField}
                  disabled={GiftCodeID}
                />
                {!GiftCodeID && (
                  <Button
                    type="submit"
                    fillMode={"solid"}
                    themeColor={"primary"}
                    style={{ marginTop: 34 }}
                    onClick={() =>
                      handleGetUserDetails(
                        formRenderProps.valueGetter("UserMobile"),
                        formRenderProps
                      )
                    }
                    disabled={formLoading}
                  >
                    View
                  </Button>
                )}
              </GridLayoutItem>
              {(formRenderProps.valueGetter("CustomerDetails") ||
                GiftCodeID) && (
                <>
                  <GridLayoutItem
                    colSpan={2}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      marginTop: 10,
                      background: "gainsboro",
                      borderRadius: "4px",
                      padding: "8px",
                    }}
                  >
                    <div>
                      <Typography.h5 className="m-0">
                        Customer Name :{" "}
                        {!GiftCodeID
                          ? formRenderProps.valueGetter("CustomerDetails")
                              ?.first_name
                          : GiftCodeDetail?.AssignUser}{" "}
                        {!GiftCodeID &&
                          formRenderProps.valueGetter("CustomerDetails")
                            ?.last_name}
                      </Typography.h5>
                    </div>
                  </GridLayoutItem>
                  {!GiftCodeID && (
                    <GridLayoutItem
                      colSpan={2}
                      className="flight-search-radio-griditem"
                    >
                      <Field
                        id={"Type"}
                        name={"Type"}
                        component={FormRadioGroup}
                        options={PROMO_TYPE}
                      />
                    </GridLayoutItem>
                  )}
                  <GridLayoutItem colSpan={1}>
                    <Field
                      name="GiftCode"
                      label="Gift Code"
                      component={FormTextField}
                      placeholder="Gift Code"
                      astrike={true}
                      disabled={
                        formRenderProps.valueGetter("Type") === "Auto" ||
                        GiftCodeID
                      }
                      validator={codeValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={1}>
                    <Field
                      name="Amount"
                      label="Amount"
                      component={FormNumericTextField}
                      placeholder="Amount"
                      astrike={true}
                      min={0}
                      validator={requiredValidator}
                      onChange={(e) => {
                        formRenderProps.onChange("MinAmtUsage", {
                          value: e?.value || 0,
                        });
                        formRenderProps.onChange("MaxAmtUsage", {
                          value: e?.value || 0,
                        });
                        formRenderProps.onChange("FlightTicketAmt", {
                          value: e?.value || 0,
                        });
                        formRenderProps.onChange("HotelBookingAmt", {
                          value: 0,
                        });
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={1}>
                    <Field
                      id={"FromDate"}
                      name={"FromDate"}
                      component={FormDatePicker}
                      label={"From Date"}
                      validator={requiredValidator}
                      minDate={moment().toDate()}
                      onChange={(e) => {
                        if (
                          moment(e.value).isAfter(
                            moment(formRenderProps?.valueGetter("ToDate"))
                          )
                        ) {
                          formRenderProps.onChange("ToDate", { value: "" });
                        }
                      }}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={1}>
                    <Field
                      id={"ToDate"}
                      name={"ToDate"}
                      component={FormDatePicker}
                      label={"To Date"}
                      minDate={
                        formRenderProps.valueGetter(`FromDate`)
                          ? moment(
                              formRenderProps.valueGetter(`FromDate`)
                            ).toDate()
                          : moment().toDate()
                      }
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={1}>
                    <Field
                      name="OnlyUsedInWallet"
                      label="Only Use In Wallet"
                      component={FormCheckbox}
                      wrapperStyle={{
                        borderRadius: "5px",
                        fontWeight: "bold",
                        fontSize: "16px",
                        padding: "10px",
                        background: "#e0e0e0",
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={1}></GridLayoutItem>
                  {!formRenderProps.valueGetter("OnlyUsedInWallet") && (
                    <>
                      <GridLayoutItem
                        colSpan={2}
                        style={{
                          borderBottom: "2px dashed gray",
                          width: "100%",
                          margin: 0,
                          paddingBottom: 14,
                        }}
                      />
                      <GridLayoutItem colSpan={1}>
                        <Field
                          id={"UsageCount"}
                          name={"UsageCount"}
                          label={"Usage Count"}
                          component={FormNumericTextField}
                          placeholder="Amount"
                          min={1}
                          max={100}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem colSpan={1}></GridLayoutItem>
                      <GridLayoutItem colSpan={1}>
                        <Field
                          name="MinAmtUsage"
                          label="Min Amount Usage"
                          component={FormNumericTextField}
                          placeholder="Min Amount Usage"
                          min={0}
                          max={formRenderProps.valueGetter("Amount") || 0}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem colSpan={1}>
                        <Field
                          name="MaxAmtUsage"
                          label="Max Amount Usage"
                          component={FormNumericTextField}
                          placeholder="Max Amount Usage"
                          min={formRenderProps.valueGetter("MinAmtUsage") || 0}
                          max={formRenderProps.valueGetter("Amount") || 0}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem
                        colSpan={2}
                        style={{
                          borderBottom: "2px dashed gray",
                          width: "100%",
                          margin: 0,
                          paddingBottom: 14,
                        }}
                      />
                      <GridLayoutItem colSpan={1}>
                        <Field
                          name={"AmountType"}
                          label={"Amount Type"}
                          component={FormSelectionField}
                          placeholder="Amount Type"
                          astrike={true}
                          validator={requiredValidator}
                          onChange={() => {
                            formRenderProps.onChange("FlightTicketAmt", {
                              value: 0,
                            });
                            formRenderProps.onChange("HotelBookingAmt", {
                              value: 0,
                            });
                          }}
                          options={DISC_TYPE.map((type: any) => {
                            return {
                              value: type.value,
                              label: type.label,
                            };
                          })}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem colSpan={1}></GridLayoutItem>
                      <GridLayoutItem colSpan={1}>
                        <Field
                          name="FlightTicketAmt"
                          label="Flight Ticket Amount"
                          component={FormNumericTextField}
                          placeholder="Flight Ticket Amount"
                          min={0}
                          max={
                            formRenderProps.valueGetter("AmountType") === "PER"
                              ? 100
                              : formRenderProps.valueGetter("Amount") || 0
                          }
                          astrike={true}
                          onChange={(e) => {
                            if (e?.value) {
                              if (
                                formRenderProps.valueGetter("AmountType") ===
                                "PER"
                              ) {
                                formRenderProps.onChange("HotelBookingAmt", {
                                  value: 100 - e.value,
                                });
                              } else {
                                formRenderProps.onChange("HotelBookingAmt", {
                                  value:
                                    (formRenderProps.valueGetter("Amount") ||
                                      0) - e.value,
                                });
                              }
                            } else {
                              formRenderProps.onChange("HotelBookingAmt", {
                                value: 0,
                              });
                            }
                          }}
                          validator={requiredValidator}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem colSpan={1}>
                        <Field
                          name="HotelBookingAmt"
                          label="Hotel Booking Amount"
                          component={FormNumericTextField}
                          placeholder="Hotel Booking Amount"
                          min={0}
                          disabled
                          // astrike={true}
                          // validator={requiredValidator}
                        />
                      </GridLayoutItem>
                    </>
                  )}
                  <GridLayoutItem
                    colSpan={2}
                    style={{
                      borderBottom: "2px dashed gray",
                      width: "100%",
                      margin: 0,
                      paddingBottom: 14,
                    }}
                  />
                  <GridLayoutItem colSpan={1}>
                    <Field
                      name="IsActive"
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      options={STATUSARRAY.map((type: any) => {
                        return {
                          value: type.value,
                          label: type.label,
                        };
                      })}
                      placeholder="Status"
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={1}
                    style={{
                      textAlign: "end",
                      marginTop: "35px",
                      width: "100%",
                    }}
                  >
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginRight: 4 }}
                      onClick={handleFormClose}
                    >
                      Cancel
                    </Button>
                    <ButtonWithLoading
                      label={GiftCodeID ? "Update" : "Create"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || formLoading}
                      loading={formLoading}
                      onClick={() => setBtnType(2)}
                    />
                  </GridLayoutItem>
                  <TypeChangeWatcher formRenderProps={formRenderProps} />
                </>
              )}
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CrateGiftCode;
